import React, { useState, useCallback, useEffect } from "react";
import { useTranslation } from "react-i18next";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  Stack,
  Slider,
  Chip,
  Autocomplete,
  IconButton,
  ToggleButton,
  ToggleButtonGroup,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";

function AdvancedFiltersModal({ open, onClose, filters, onFiltersChange }) {
  const { t } = useTranslation();
  const [localFilters, setLocalFilters] = useState(filters);

  // Sync local filters when modal opens or filters prop changes
  useEffect(() => {
    if (open) {
      setLocalFilters(filters);
    }
  }, [open, filters]);

  const handleFilterChange = useCallback((key, value) => {
    setLocalFilters((prev) => ({ ...prev, [key]: value }));
  }, []);

  const handleApply = useCallback(() => {
    onFiltersChange(localFilters);
    onClose();
  }, [localFilters, onFiltersChange, onClose]);

  const handleReset = useCallback(() => {
    const resetFilters = {
      search: "",
      contractPriceFrom: "any",
      contractPriceTo: "any",
      capitalAtRiskFrom: "any",
      capitalAtRiskTo: "any",
      contentsCapitalAtRiskFrom: "any",
      contentsCapitalAtRiskTo: "any",
      fireRiskRange: [0, 1],
      floodRiskRange: [0, 1],
      earthquakeRiskRange: [0, 1],
      certainAddress: "all",
      earthquakeCoverage: "all",
    };
    setLocalFilters(resetFilters);
    onFiltersChange(resetFilters);
  }, [onFiltersChange]);

  const formatPercentage = (value) => {
    return `${Math.round(value * 100)}%`;
  };

  const anyLabel = t("common.any");
  const fromLabel = t("common.from");
  const toLabel = t("common.to");

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        {t("portfolioMap.advancedFilters")}
        <IconButton onClick={onClose} size="small" sx={{ ml: 1 }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <Stack spacing={3} sx={{ mt: 1 }}>
          <Box display="grid" gridTemplateColumns="1fr 1fr" gap={2}>
            {/* Address Filter */}
            <Box>
              <Typography variant="subtitle2" marginBottom={1}>
                {t("portfolioMap.verifiedAddress")}
              </Typography>
              <ToggleButtonGroup
                value={localFilters.certainAddress || "all"}
                exclusive
                onChange={(_, newValue) => {
                  if (newValue !== null) {
                    handleFilterChange("certainAddress", newValue);
                  }
                }}
                fullWidth
                size="small"
              >
                <ToggleButton value="all">{t("common.all")}</ToggleButton>
                <ToggleButton value="yes">{t("common.yes")}</ToggleButton>
                <ToggleButton value="no">{t("common.no")}</ToggleButton>
              </ToggleButtonGroup>
            </Box>

            {/* Earthquake Coverage Filter */}
            <Box>
              <Typography variant="subtitle2" marginBottom={1}>
                {t("portfolioMap.earthquakeCoverage")}
              </Typography>
              <ToggleButtonGroup
                value={localFilters.earthquakeCoverage || "all"}
                exclusive
                onChange={(_, newValue) => {
                  if (newValue !== null) {
                    handleFilterChange("earthquakeCoverage", newValue);
                  }
                }}
                fullWidth
                size="small"
              >
                <ToggleButton value="all">{t("common.all")}</ToggleButton>
                <ToggleButton value="yes">{t("common.yes")}</ToggleButton>
                <ToggleButton value="no">{t("common.no")}</ToggleButton>
              </ToggleButtonGroup>
            </Box>
          </Box>

          {/* Contract Price Range */}
          <Box>
            <Typography variant="subtitle2" marginBottom={1}>
              {t("portfolioMap.contractPriceRange")}
            </Typography>
            <Stack direction="row" spacing={2}>
              <Autocomplete
                freeSolo
                options={[
                  "any",
                  ...Array.from({ length: 20 }, (_, i) => 100 + i * 100),
                ]}
                value={localFilters.contractPriceFrom || "any"}
                onChange={(_, newValue) => {
                  const value = newValue === null ? "any" : newValue;
                  handleFilterChange("contractPriceFrom", value);
                }}
                onInputChange={(_, newInputValue) => {
                  // Handle custom input values
                  if (
                    newInputValue !== "" &&
                    !isNaN(newInputValue) &&
                    newInputValue !== "any"
                  ) {
                    handleFilterChange(
                      "contractPriceFrom",
                      parseInt(newInputValue),
                    );
                  } else if (newInputValue === "") {
                    handleFilterChange("contractPriceFrom", "any");
                  }
                }}
                filterOptions={(options, params) => {
                  // Always show all options, don't filter based on input
                  return options;
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={fromLabel}
                    size="small"
                    helperText={t("portfolioMap.customValueHelper")}
                  />
                )}
                getOptionLabel={(option) => {
                  if (option === "any") return anyLabel;
                  return option.toString();
                }}
                renderOption={(props, option) => (
                  <Box component="li" {...props}>
                    {option === "any" ? anyLabel : `€${option}`}
                  </Box>
                )}
              />
              <Autocomplete
                freeSolo
                options={[
                  "any",
                  ...Array.from({ length: 20 }, (_, i) => 100 + i * 100),
                ]}
                value={localFilters.contractPriceTo || "any"}
                onChange={(_, newValue) => {
                  const value = newValue === null ? "any" : newValue;
                  handleFilterChange("contractPriceTo", value);
                }}
                onInputChange={(_, newInputValue) => {
                  // Handle custom input values
                  if (
                    newInputValue !== "" &&
                    !isNaN(newInputValue) &&
                    newInputValue !== "any"
                  ) {
                    handleFilterChange(
                      "contractPriceTo",
                      parseInt(newInputValue),
                    );
                  } else if (newInputValue === "") {
                    handleFilterChange("contractPriceTo", "any");
                  }
                }}
                filterOptions={(options, params) => {
                  // Always show all options, don't filter based on input
                  return options;
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={toLabel}
                    size="small"
                    helperText={t("portfolioMap.customValueHelper")}
                  />
                )}
                getOptionLabel={(option) => {
                  if (option === "any") return anyLabel;
                  return option.toString();
                }}
                renderOption={(props, option) => (
                  <Box component="li" {...props}>
                    {option === "any" ? anyLabel : `€${option}`}
                  </Box>
                )}
              />
            </Stack>
          </Box>

          {/* Capital at Risk Range */}
          <Box>
            <Typography variant="subtitle2" marginBottom={1}>
              {t("portfolioMap.capitalAtRiskRange")}
            </Typography>
            <Stack direction="row" spacing={2}>
              <Autocomplete
                freeSolo
                options={[
                  "any",
                  ...Array.from({ length: 60 }, (_, i) => 50000 + i * 50000),
                ]}
                value={localFilters.capitalAtRiskFrom || "any"}
                onChange={(_, newValue) => {
                  const value = newValue === null ? "any" : newValue;
                  handleFilterChange("capitalAtRiskFrom", value);
                }}
                onInputChange={(_, newInputValue) => {
                  // Handle custom input values
                  if (
                    newInputValue !== "" &&
                    !isNaN(newInputValue) &&
                    newInputValue !== "any"
                  ) {
                    handleFilterChange(
                      "capitalAtRiskFrom",
                      parseInt(newInputValue),
                    );
                  } else if (newInputValue === "") {
                    handleFilterChange("capitalAtRiskFrom", "any");
                  }
                }}
                filterOptions={(options, params) => {
                  // Always show all options, don't filter based on input
                  return options;
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={fromLabel}
                    size="small"
                    helperText={t("portfolioMap.customValueHelper")}
                  />
                )}
                getOptionLabel={(option) => {
                  if (option === "any") return anyLabel;
                  return option.toString();
                }}
                renderOption={(props, option) => (
                  <Box component="li" {...props}>
                    {option === "any"
                      ? anyLabel
                      : `€${option.toLocaleString()}`}
                  </Box>
                )}
              />
              <Autocomplete
                freeSolo
                options={[
                  "any",
                  ...Array.from({ length: 60 }, (_, i) => 50000 + i * 50000),
                ]}
                value={localFilters.capitalAtRiskTo || "any"}
                onChange={(_, newValue) => {
                  const value = newValue === null ? "any" : newValue;
                  handleFilterChange("capitalAtRiskTo", value);
                }}
                onInputChange={(_, newInputValue) => {
                  // Handle custom input values
                  if (
                    newInputValue !== "" &&
                    !isNaN(newInputValue) &&
                    newInputValue !== "any"
                  ) {
                    handleFilterChange(
                      "capitalAtRiskTo",
                      parseInt(newInputValue),
                    );
                  } else if (newInputValue === "") {
                    handleFilterChange("capitalAtRiskTo", "any");
                  }
                }}
                filterOptions={(options, params) => {
                  // Always show all options, don't filter based on input
                  return options;
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={toLabel}
                    size="small"
                    helperText={t("portfolioMap.customValueHelper")}
                  />
                )}
                getOptionLabel={(option) => {
                  if (option === "any") return anyLabel;
                  return option.toString();
                }}
                renderOption={(props, option) => (
                  <Box component="li" {...props}>
                    {option === "any"
                      ? anyLabel
                      : `€${option.toLocaleString()}`}
                  </Box>
                )}
              />
            </Stack>
          </Box>

          {/* Contents Capital at Risk Range */}
          <Box>
            <Typography variant="subtitle2" marginBottom={1}>
              {t("portfolioMap.contentsCapitalAtRiskRange")}
            </Typography>
            <Stack direction="row" spacing={2}>
              <Autocomplete
                freeSolo
                options={[
                  "any",
                  ...Array.from({ length: 60 }, (_, i) => 50000 + i * 50000),
                ]}
                value={localFilters.contentsCapitalAtRiskFrom || "any"}
                onChange={(_, newValue) => {
                  const value = newValue === null ? "any" : newValue;
                  handleFilterChange("contentsCapitalAtRiskFrom", value);
                }}
                onInputChange={(_, newInputValue) => {
                  // Handle custom input values
                  if (
                    newInputValue !== "" &&
                    !isNaN(newInputValue) &&
                    newInputValue !== "any"
                  ) {
                    handleFilterChange(
                      "contentsCapitalAtRiskFrom",
                      parseInt(newInputValue),
                    );
                  } else if (newInputValue === "") {
                    handleFilterChange("contentsCapitalAtRiskFrom", "any");
                  }
                }}
                filterOptions={(options, params) => {
                  // Always show all options, don't filter based on input
                  return options;
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={fromLabel}
                    size="small"
                    helperText={t("portfolioMap.customValueHelper")}
                  />
                )}
                getOptionLabel={(option) => {
                  if (option === "any") return anyLabel;
                  return option.toString();
                }}
                renderOption={(props, option) => (
                  <Box component="li" {...props}>
                    {option === "any"
                      ? anyLabel
                      : `€${option.toLocaleString()}`}
                  </Box>
                )}
              />
              <Autocomplete
                freeSolo
                options={[
                  "any",
                  ...Array.from({ length: 60 }, (_, i) => 50000 + i * 50000),
                ]}
                value={localFilters.contentsCapitalAtRiskTo || "any"}
                onChange={(_, newValue) => {
                  const value = newValue === null ? "any" : newValue;
                  handleFilterChange("contentsCapitalAtRiskTo", value);
                }}
                onInputChange={(_, newInputValue) => {
                  // Handle custom input values
                  if (
                    newInputValue !== "" &&
                    !isNaN(newInputValue) &&
                    newInputValue !== "any"
                  ) {
                    handleFilterChange(
                      "contentsCapitalAtRiskTo",
                      parseInt(newInputValue),
                    );
                  } else if (newInputValue === "") {
                    handleFilterChange("contentsCapitalAtRiskTo", "any");
                  }
                }}
                filterOptions={(options, params) => {
                  // Always show all options, don't filter based on input
                  return options;
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={toLabel}
                    size="small"
                    helperText={t("portfolioMap.customValueHelper")}
                  />
                )}
                getOptionLabel={(option) => {
                  if (option === "any") return anyLabel;
                  return option.toString();
                }}
                renderOption={(props, option) => (
                  <Box component="li" {...props}>
                    {option === "any"
                      ? anyLabel
                      : `€${option.toLocaleString()}`}
                  </Box>
                )}
              />
            </Stack>
          </Box>

          {/* Fire Risk Range */}
          <Box sx={{ padding: "0px 15px" }}>
            <Typography variant="subtitle2" gutterBottom>
              {t("portfolioMap.fireRiskRange")}
            </Typography>
            <Slider
              value={localFilters.fireRiskRange}
              onChange={(_, value) =>
                handleFilterChange("fireRiskRange", value)
              }
              valueLabelDisplay="auto"
              valueLabelFormat={formatPercentage}
              min={0}
              max={1}
              step={0.01}
              marks={[
                { value: 0, label: "0%" },
                { value: 0.5, label: "50%" },
                { value: 1, label: "100%" },
              ]}
            />
            <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
              <Chip
                label={`${t("common.min")}: ${formatPercentage(
                  localFilters.fireRiskRange[0],
                )}`}
                size="small"
                variant="outlined"
              />
              <Chip
                label={`${t("common.max")}: ${formatPercentage(
                  localFilters.fireRiskRange[1],
                )}`}
                size="small"
                variant="outlined"
              />
            </Stack>
          </Box>

          {/* Flood Risk Range */}
          <Box sx={{ padding: "0px 15px" }}>
            <Typography variant="subtitle2" gutterBottom>
              {t("portfolioMap.floodRiskRange")}
            </Typography>
            <Slider
              value={localFilters.floodRiskRange}
              onChange={(_, value) =>
                handleFilterChange("floodRiskRange", value)
              }
              valueLabelDisplay="auto"
              valueLabelFormat={formatPercentage}
              min={0}
              max={1}
              step={0.01}
              marks={[
                { value: 0, label: "0%" },
                { value: 0.5, label: "50%" },
                { value: 1, label: "100%" },
              ]}
            />
            <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
              <Chip
                label={`${t("common.min")}: ${formatPercentage(
                  localFilters.floodRiskRange[0],
                )}`}
                size="small"
                variant="outlined"
              />
              <Chip
                label={`${t("common.max")}: ${formatPercentage(
                  localFilters.floodRiskRange[1],
                )}`}
                size="small"
                variant="outlined"
              />
            </Stack>
          </Box>

          {/* Earthquake Risk Range */}
          <Box sx={{ padding: "0px 15px" }}>
            <Typography variant="subtitle2" gutterBottom>
              {t("portfolioMap.earthquakeRiskRange")}
            </Typography>
            <Slider
              value={localFilters.earthquakeRiskRange}
              onChange={(_, value) =>
                handleFilterChange("earthquakeRiskRange", value)
              }
              valueLabelDisplay="auto"
              valueLabelFormat={formatPercentage}
              min={0}
              max={1}
              step={0.01}
              marks={[
                { value: 0, label: "0%" },
                { value: 0.5, label: "50%" },
                { value: 1, label: "100%" },
              ]}
            />
            <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
              <Chip
                label={`${t("common.min")}: ${formatPercentage(
                  localFilters.earthquakeRiskRange[0],
                )}`}
                size="small"
                variant="outlined"
              />
              <Chip
                label={`${t("common.max")}: ${formatPercentage(
                  localFilters.earthquakeRiskRange[1],
                )}`}
                size="small"
                variant="outlined"
              />
            </Stack>
          </Box>
        </Stack>
      </DialogContent>
      <DialogActions sx={{ padding: "12px" }}>
        <Button onClick={handleReset}>{t("common.reset")}</Button>
        <Button onClick={onClose}>{t("common.cancel")}</Button>
        <Button onClick={handleApply} variant="contained">
          {t("portfolioMap.applyFilters")}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default AdvancedFiltersModal;
